<template lang="pug" src="./form-answers.pug"></template>
<style lang="scss" src="./form-answers.scss" scoped></style>

<script>
import { mapState, mapGetters } from 'vuex';
import config from '../../../../config.json';

import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { normalizedPhone, vLength } from '@/utils/helpers';
import validate from '@/mixins/validate';
import request from '@/utils/request';

const REQUEST_TIMEOUT = 1000;

export default {
    mixins: [validate, validationMixin],
    data: () => ({
        loading: false,
        requestProcessing: false,
        filters: {
            phoneNumber: null,
            status: null
        },
        formAnswerData: {
            formId: null,
            phoneNumber: null,
            status: null,
            answers: []
        },
        comment: null,
        isEdit: false,
        showPhoneCheck: true,
        isSearchActive: false
    }),
    validations: {
        formAnswerData: {
            formId: {
                required
            },
            phoneNumber: {
                required,
                length: vLength(9)
            },
            status: {
                required: requiredIf(function() {
                    return this.canEditForm;
                })
            }
        }
    },
    computed: {
        ...mapGetters(['roleName']),
        ...mapState({
            items: state => state.formAnswers.items,
            pagination: state => state.formAnswers.pagination,
            forms: state => state.forms.allItems
        }),
        headers() {
            const titles = ['id', 'status', 'comment', 'client', 'phoneNumber', 'form', 'createdAt'];

            if (!this.isSearchActive) {
                titles.push('actions');
            }

            if (['admin'].includes(this.roleName)) {
                titles.splice(titles.length - 1, 0, 'operator');
            }

            return titles.map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.FormAnswers.table.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        statusesSelect() {
            return config.forms.statuses.map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.FormAnswers.statuses.${it}`),
                value: it
            }));
        },
        simpleQuestionItems() {
            return ['yes', 'no'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.answers.${it}`),
                value: it
            }));
        },
        isFieldDisabled() {
            return this.isEdit && this.canEditForm;
        },
        canEditForm() {
            return Object.values(this.formAnswerData).every(it => Array.isArray(it) ? !!it.length : !!it);
        }
    },
    created() {
        this.$store.dispatch('forms/getItems', {
            fetchAll: true
        });

        this.loadItems();
    },
    methods: {
        loadItems(page = 1) {
            if (this.loading) return;

            this.loading = true;

            const payload = {
                ...this.filters,
                page
            };

            if (this.isSearchActive) {
                payload.phoneNumber = this.formAnswerData.phoneNumber;
                payload.search = true;
            }

            this.$store.dispatch('formAnswers/getItems', payload).then(() => {
                this.$nextTick(() => this.loading = false);
            });
        },

        onPhoneNumberCheck() {
            if (this.$v.formAnswerData.phoneNumber.$invalid) {
                this.$v.formAnswerData.phoneNumber.$touch();
                this.focusFirstErrorInput();
                return;
            }

            this.isSearchActive = true;
            this.loadItems();
        },

        onEditButtonClick() {
            this.isEdit = false;
            this.showPhoneCheck = false;
            this.isSearchActive = false;
            this.loadItems();
        },

        onPageChange(page) {
            if (page === this.pagination.page) {
                return;
            }

            const isSearchMode = !this.showPhoneCheck;
            this.loadItems(page, isSearchMode);
        },

        createFormAnswer() {
            if (this.$v.formAnswerData.$invalid) {
                this.$v.formAnswerData.$touch();
                this.focusFirstErrorInput();
                return;
            }

            if (this.requestProcessing) return;
            this.requestProcessing = true;

            this.$store.dispatch('formAnswers/create', {
                ...Object.select(this.formAnswerData, ['phoneNumber', 'formId'])
            }).then(id => {
                if (id) {
                    this.formAnswerData = Object.assign({}, {
                        ...this.formAnswerData,
                        id, status: 'new'
                    });
                }

                this.loadComponents(this.formAnswerData.formId);

                this.$nextTick(() => {
                    this.showPhoneCheck = false;
                    this.isSearchActive = false;
                    this.$v.formAnswerData.$reset();
                    this.loadItems();
                });
            }).finally(() => {
                setTimeout(() => {
                    this.requestProcessing = false;
                }, REQUEST_TIMEOUT);
            });
        },

        async loadComponents(formId) {
            await request({ method: 'get', url: `forms/${formId}/components` })
                .then(res => {
                    if (res.data) {
                        const answers = res.data.map(it => ({
                            id: it.id,
                            type: it.type,
                            text: it.text,
                            answer: it.type === 'multiple-choice' ? [] : '',
                            options: it.options || []
                        }));

                        this.$set(this.formAnswerData, 'answers', answers);
                    }
                });
        },

        async setActiveFormAnswer(item) {
            this.isEdit = true;
            this.resetData();

            const {
                id, status, answers,
                form: { id: formId },
                client, comment
            } = item;

            this.formAnswerData = Object.assign({}, {
                id, formId, status,
                phoneNumber: normalizedPhone(client.user.phoneNumber)
            });

            this.comment = comment;

            if (answers == 0) {
                await this.loadComponents(formId);
                return;
            }

            await request({
                method: 'get',
                url: `/answers/${id}`
            }).then(res => {
                if (res.data) {
                    const answers = res.data.answers.map(it => ({
                        id: it.formComponentId,
                        options: it.formComponent.options || [],
                        answer: it.answer || [],
                        text: it.formComponent.text,
                        type: it.formComponent.type
                    }));

                    this.$set(this.formAnswerData, 'answers', answers);
                }
            });
        },

        processAnswer(value, item) {
            const alreadyExistIndex = this.formAnswerData.answers.findIndex(it => it.id === item.id);

            if (alreadyExistIndex !== -1) {
                this.$set(this.formAnswerData.answers[alreadyExistIndex], 'answer', value);
                return;
            }

            const { id, type } = item;

            this.formAnswerData.answers.push({
                id, type
            });
        },

        saveFormAnswer() {
            if (this.$v.formAnswerData.$invalid) {
                this.$v.formAnswerData.$touch();
                return;
            }

            if (this.requestProcessing) return;
            this.requestProcessing = true;

            const data = Object.assign(this.formAnswerData, {
                comment: this.comment
            });

            this.$store.dispatch('formAnswers/save', data)
                .then(() => {
                    this.showPhoneCheck = true;
                    this.resetData();
                })
                .finally(() => {
                    setTimeout(() => {
                        this.requestProcessing = false;
                    }, REQUEST_TIMEOUT);
                });
        },

        cancelEdit() {
            this.showPhoneCheck = true;
            this.isSearchActive = false;
            this.isEdit = false;
            this.loadItems();
            this.resetData();
        },

        clearAllData() {
            this.isEdit = false;
            this.showPhoneCheck = true;
            this.isSearchActive = false;

            this.filters = {
                phoneNumber: null,
                status: null
            };

            this.resetData();
            this.loadItems();
        },

        resetData() {
            this.$v.formAnswerData.$reset();

            this.formAnswerData = Object.assign({}, {
                phoneNumber: null,
                formId: null,
                status: null,
                answers: []
            });

            this.comment = null;
        }
    }
};
</script>
